import { Application } from '@platform/client';
import {
  ProfessionalApplicationStatusEnum,
  SAPApplicationParameters,
} from '@platform/client/schemas';
import { Route, Switch, useLocation } from 'wouter';
import { ApplicationStorageDataKeys, Step } from '../Application';
import { useApplicationLocalStorage } from '../application-hook';
import { ExperienceHopeSelection } from '../final-details/ExperienceHopeSelection';
import { SAPSubmission } from '../final-details/sap/SAPSubmission';
import { ExperienceDetailSelection } from '../profile/shared/ExperienceDetailSelection';
import { LocationSelection } from '../profile/shared/LocationSelection';
import { ProfilePictureUpload } from '../profile/shared/ProfilePictureUpload';
import { ResumeSelection } from '../profile/shared/ResumeSelection';
import { SAPCohortSelection } from '../program/sap/SAPCohortSelection';
import { SAPWorkExperience } from '../program/sap/SAPWorkExperience';
import { SAPTeamSelection } from '../program/sap/TeamSelection';
import { ImpactAreaSelection } from '../program/shared/ImpactAreaSelection';
import { SAPApprovedEvergreen } from '../result/sap/SAPApplicationEvergreen';
import { ApplicationApproved } from '../result/shared/ApplicationApproved';
import { ApplicationRejected } from '../result/shared/ApplicationRejected';
import { SkillsImpactSelection } from '../skills/SkillsImpactSelection';
import { SkillsExperience } from '../skills/SkillsSelection';

enum SAPApplicationStep {
  COHORT_SELECTION = 'COHORT_SELECTION',
  TEAMS_SELECTION = 'TEAMS_SELECTION',
  SAP_EXPERIENCE_SELECTION = 'SAP_EXPERIENCE_SELECTION',
  IMPACT_AREA_SELECTION = 'IMPACT_AREA_SELECTION',
  LOCATION_SELECTION = 'LOCATION_SELECTION',
  RESUME_SELECTION = 'RESUME_SELECTION',
  EXPERIENCE_SELECTION = 'EXPERIENCE_SELECTION',
  PHOTO_SELECTION = 'PHOTO_SELECTION',

  SKILLS_SELECTION = 'SKILLS_SELECTION',
  SKILL_IMPACT_SELECTION = 'SKILL_IMPACT_SELECTION',

  EXPERIENCE_HOPE_SELECTION = 'EXPERIENCE_HOPE_SELECTION',
  REFERRAL_SELECTION = 'REFERRAL_SELECTION',
}

const SAPApplicationSteps: Record<SAPApplicationStep, Step> = {
  [SAPApplicationStep.COHORT_SELECTION]: {
    progress: 20,
    component: SAPCohortSelection,
    url: '/',
  },
  [SAPApplicationStep.TEAMS_SELECTION]: {
    progress: 40,
    component: SAPTeamSelection,
    url: '/sap-teams-selection',
  },
  [SAPApplicationStep.SAP_EXPERIENCE_SELECTION]: {
    progress: 60,
    component: SAPWorkExperience,
    url: '/sap-experience-selection',
  },
  [SAPApplicationStep.IMPACT_AREA_SELECTION]: {
    progress: 80,
    component: ImpactAreaSelection,
    url: '/impact-area-selection',
  },
  [SAPApplicationStep.LOCATION_SELECTION]: {
    progress: 135,
    component: LocationSelection,
    url: '/location-selection',
  },
  [SAPApplicationStep.RESUME_SELECTION]: {
    progress: 150,
    component: ResumeSelection,
    url: '/resume-selection',
  },
  [SAPApplicationStep.EXPERIENCE_SELECTION]: {
    progress: 170,
    component: ExperienceDetailSelection,
    url: '/experience-selection',
  },
  [SAPApplicationStep.PHOTO_SELECTION]: {
    progress: 190,
    component: ProfilePictureUpload,
    url: '/photo-selection',
  },
  [SAPApplicationStep.SKILLS_SELECTION]: {
    progress: 235,
    component: SkillsExperience,
    url: '/skills-selection',
  },
  [SAPApplicationStep.SKILL_IMPACT_SELECTION]: {
    progress: 275,
    component: SkillsImpactSelection,
    url: '/skill-impact-selection',
  },
  [SAPApplicationStep.EXPERIENCE_HOPE_SELECTION]: {
    progress: 340,
    component: ExperienceHopeSelection,
    url: '/experience-hope-selection',
  },
  [SAPApplicationStep.REFERRAL_SELECTION]: {
    progress: 385,
    component: SAPSubmission,
    url: '/referral-selection',
  },
} as const;

export const SAPApplicationRoot = () => {
  const [location, navigate] = useLocation();

  const { updateApplicationData, getApplicationDataByKey } =
    useApplicationLocalStorage<SAPApplicationParameters>(
      ApplicationStorageDataKeys.PROFESSIONAL_APPLICATION,
    );

  const goToNextStep = () => {
    const steps = Object.values(SAPApplicationStep);
    const currentIndex = steps.findIndex((step) => {
      return SAPApplicationSteps[step as SAPApplicationStep].url === location;
    });

    if (currentIndex === 0) {
      if (Number(getApplicationDataByKey('cohort')) === 1) {
        updateApplicationData({
          teamSelection: 'individual',
        });

        const nextStep = steps[currentIndex + 2] as SAPApplicationStep; // Skip team selection for evergreen cohort
        const nextUrl = SAPApplicationSteps[nextStep as SAPApplicationStep].url;
        navigate(nextUrl);
        return nextStep as SAPApplicationStep;
      }
    }

    const nextStep =
      currentIndex < steps.length - 1 ? steps[currentIndex + 1] : steps[currentIndex];
    const nextUrl = SAPApplicationSteps[nextStep as SAPApplicationStep].url;
    navigate(nextUrl);
    return nextStep as SAPApplicationStep;
  };

  const goToPreviousStep = () => {
    const steps = Object.values(SAPApplicationStep);
    const currentIndex = steps.findIndex(
      (step) => SAPApplicationSteps[step as SAPApplicationStep].url === location,
    );

    if (currentIndex === 2) {
      if (Number(getApplicationDataByKey('cohort')) === 1) {
        const nextStep = steps[currentIndex - 2] as SAPApplicationStep; // Skip team selection for evergreen cohort
        const nextUrl = SAPApplicationSteps[nextStep as SAPApplicationStep].url;
        navigate(nextUrl);
        return nextStep as SAPApplicationStep;
      }
    }

    if (currentIndex === 0) {
      window.location.href = 'platform/auth/application-start?partner=sap';
      return;
    }

    const previousStep = steps[currentIndex - 1];
    const previousUrl = SAPApplicationSteps[previousStep as SAPApplicationStep].url;
    navigate(previousUrl);
    return previousStep as SAPApplicationStep;
  };

  const applicationData = Application.useGetProfessionalApplicationStatus();

  if (applicationData.data?.applicationStatus === ProfessionalApplicationStatusEnum.rejected) {
    return <ApplicationRejected programName='Acceleration Collective' />;
  }

  if (applicationData.data?.applicationStatus === ProfessionalApplicationStatusEnum.approved) {
    if (applicationData.data.cohortId === 1) {
      return <SAPApprovedEvergreen />;
    }
    return <ApplicationApproved />;
  }

  return (
    <Switch>
      {Object.entries(SAPApplicationSteps).map(([_key, value]) => {
        const { url, component: Component, progress } = value;
        return (
          <Route key={url} path={url}>
            <Component
              onBack={goToPreviousStep}
              onContinue={goToNextStep}
              progress={progress}
              dataKey={ApplicationStorageDataKeys.PROFESSIONAL_APPLICATION}
            />
          </Route>
        );
      })}
    </Switch>
  );
};
